import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  DISCREPANCY_RUN_STATUS_LABELS,
  DISCREPANCY_RUN_STATUS_COLORS,
  DISCREPANCY_RUN_STATUSES,
} from '../../../constants/discrepancyMappingStatuses';
import { SummaryGrid, SummaryTile, SummaryLabel, SummaryValue, SummaryMeta } from './styles';
import { getChipSx } from './chipStyles';
import { describeRunTrigger, runTriggerStyle } from '../../../utils/runTriggers';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

// Truncate long error messages so the tile stays compact; full text is in the tooltip.
const ERROR_PREVIEW_LIMIT = 80;
const truncate = (s) => (s && s.length > ERROR_PREVIEW_LIMIT
  ? `${s.slice(0, ERROR_PREVIEW_LIMIT)}…`
  : s);

const SummaryCard = ({ run }) => {
  if (!run) {
    return null;
  }
  const total = run.totalCount ?? 0;
  const matches = run.matchCount ?? 0;
  const mismatches = run.mismatchCount ?? 0;
  const pct = total > 0 ? Math.round((matches / total) * 1000) / 10 : null;

  const isFailed = run.status === DISCREPANCY_RUN_STATUSES.FAILED;
  const errorMessage = run.errorMessage || null;

  return (
    <SummaryGrid>
      <SummaryTile>
        <SummaryLabel>Source</SummaryLabel>
        <SummaryValue>{run.sourceSystem}</SummaryValue>
        {/* ADR-134: how the run started */}
        <SummaryMeta style={runTriggerStyle(run)}>{describeRunTrigger(run)}</SummaryMeta>
      </SummaryTile>
      <SummaryTile>
        <SummaryLabel>Status</SummaryLabel>
        <SummaryValue>
          <Chip
            size="small"
            label={DISCREPANCY_RUN_STATUS_LABELS[run.status] || run.status}
            sx={getChipSx(DISCREPANCY_RUN_STATUS_COLORS[run.status])}
          />
        </SummaryValue>
        <SummaryMeta>
          Started: {fmtDate(run.startedAt)}<br />
          Finished: {fmtDate(run.finishedAt)}
          {isFailed && (
            <>
              <br />
              <Tooltip title={errorMessage || 'No error message recorded'} arrow>
                <span style={{ color: '#d32f2f' }}>
                  Error: {truncate(errorMessage) || '(no message)'}
                </span>
              </Tooltip>
            </>
          )}
        </SummaryMeta>
      </SummaryTile>
      <SummaryTile>
        <SummaryLabel>Total</SummaryLabel>
        <SummaryValue>{total}</SummaryValue>
        <SummaryMeta>consultants compared</SummaryMeta>
      </SummaryTile>
      <SummaryTile>
        <SummaryLabel>Matches</SummaryLabel>
        <SummaryValue>{matches}</SummaryValue>
        <SummaryMeta>{pct !== null ? `${pct}% match rate` : '—'}</SummaryMeta>
      </SummaryTile>
      <SummaryTile>
        <SummaryLabel>Mismatches</SummaryLabel>
        <SummaryValue>{mismatches}</SummaryValue>
        <SummaryMeta>need investigation</SummaryMeta>
      </SummaryTile>
      {/* ADR-130: Region-scoped population check */}
      <SummaryTile>
        <SummaryLabel>Radar only</SummaryLabel>
        <SummaryValue>{run.radarOnlyCount ?? 0}</SummaryValue>
        <SummaryMeta>
          active in this source&apos;s Regions, missing from its feed
        </SummaryMeta>
      </SummaryTile>
      {(run.unscopedActiveCount ?? 0) > 0 && (
        <SummaryTile>
          <SummaryLabel>Not checked</SummaryLabel>
          <SummaryValue style={{ color: '#B45309' }}>{run.unscopedActiveCount}</SummaryValue>
          <SummaryMeta>
            active people in no Region with a system of record — fix their Region or decide the
            Region&apos;s system of record
          </SummaryMeta>
        </SummaryTile>
      )}
    </SummaryGrid>
  );
};

export default SummaryCard;
